/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';
import { usePermissions } from '../../providers/Permissions.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';

type HookProps = {
  api: vp.Api;
};

type ReturnTuple = [
  vp.ELinxSettingsResponse | undefined,
  (requestParams: vp.PortalUser) => void,
  () => void,
  (requestParams: vp.PortalUser) => Promise<vp.ELinxSettingsResponse>,
];

function useFetchPaylinkSettings({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<vp.PortalUser>();
  const [response, setResponse] = useState<vp.ELinxSettingsResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  const { permissions } = usePermissions();
  const { PayLinkEnabled } = useLockedFeatures();

  const defaultSettings: vp.ELinxSettingsResponse = {
    Success: true,
    ErrorMessage: '',
    ELinxSettings: {
      EnableELinxDecline: '0',
      ELinxReminderEnabled: '0',
      ReceiverNotificationsEnabled: '0',
      SenderNotificationsEnabled: '0',
      ELinxExpirationTime: 0,
      ELinxSenderEmailAddress: '',
      DefaultNotificationMethod: 'email',
      ELinxReminderFrequency: '',
    },
  };

  useEffect(() => {
    const sendRequest = async (requestParams: vp.PortalUser) => {
      setDataFetchingInfo(true, 'loading some data', 'fullScreen');

      const res = await api.fetchPaylinkSettings(requestParams, source);

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: vp.PortalUser) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
    (requestParams: vp.PortalUser) => {
      if (!permissions.ViewElinxSettings || !PayLinkEnabled) {
        return Promise.resolve(defaultSettings);
      }
      return api.fetchPaylinkSettings(requestParams, source);
    },
  ];
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchPaylinkSettings);
